import React from 'react';
import { useAppTheme } from '../../context/ThemeContext';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}) => {
  const { themeConfig } = useAppTheme();

  const getButtonStyles = () => {
    const baseStyles = 'inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
    
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg'
    };

    const variantStyles = {
      primary: {
        bg: themeConfig.primary,
        text: '#FFFFFF',
        hover: themeConfig.primary + '90',
        focus: themeConfig.primary + '40'
      },
      secondary: {
        bg: themeConfig.secondary,
        text: '#FFFFFF',
        hover: themeConfig.secondary + '90',
        focus: themeConfig.secondary + '40'
      },
      outline: {
        bg: 'transparent',
        text: themeConfig.text,
        border: `1px solid ${themeConfig.accent}`,
        hover: themeConfig.accent + '10',
        focus: themeConfig.accent + '20'
      },
      ghost: {
        bg: 'transparent',
        text: themeConfig.text,
        hover: themeConfig.accent + '10',
        focus: themeConfig.accent + '20'
      },
      danger: {
        bg: themeConfig.error,
        text: '#FFFFFF',
        hover: themeConfig.error + '90',
        focus: themeConfig.error + '40'
      }
    };

    const styles = variantStyles[variant];
    const widthStyle = fullWidth ? 'w-full' : '';
    const disabledStyle = (disabled || isLoading) ? 'opacity-50 cursor-not-allowed' : '';

    return `${baseStyles} ${sizeStyles[size]} ${widthStyle} ${disabledStyle} ${className} bg-[${styles.bg}] text-[${styles.text}] hover:bg-[${styles.hover}] focus:ring-[${styles.focus}]`;
  };

  return (
    <button
      className={getButtonStyles()}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};

export default Button; 